/**
 * Importação de contatos da agenda do celular → clientes.
 *
 * Lógica pura (sem Capacitor, sem React) para ser testável: normalização de
 * telefone/nome, mapeamento contato → colunas de `clients`, deduplicação
 * interna da agenda e detecção de "já cadastrado".
 *
 * Decisão de escopo: importamos SÓ nome/telefone/e-mail. O endereço da agenda
 * vem em texto livre e bagunçado, enquanto o app tem campos estruturados
 * (CEP/IBGE/UF) usados na NFS-e — importar aquilo sujaria o cadastro fiscal.
 *
 * ⚠️ O ANIVERSÁRIO TAMBÉM FICA DE FORA, e o motivo é diferente do endereço.
 * `clients.birth_date` é `DATE` — exige dia, mês E ANO. Na agenda do celular o
 * aniversário costuma vir **sem o ano** (muita gente só guarda dia/mês), e o
 * plugin devolve o campo assim mesmo. As duas saídas possíveis seriam ruins e
 * silenciosas: inventar um ano CORROMPE o dado (e ninguém teria como saber que
 * ele é falso), e descartar quem não tem ano perderia a maioria dos contatos
 * sem avisar. Enquanto a coluna exigir ano, importar aqui é pior que não
 * importar. Se um dia isso mudar, a decisão é do TIPO da coluna, não deste
 * arquivo.
 */

/** Subconjunto de `ContactPayload` (@capacitor-community/contacts) que usamos. */
export interface DeviceContactName {
  display?: string | null;
  given?: string | null;
  middle?: string | null;
  family?: string | null;
}

export interface DeviceContactPhone {
  type?: string | null;
  label?: string | null;
  isPrimary?: boolean | null;
  number?: string | null;
}

export interface DeviceContactEmail {
  type?: string | null;
  isPrimary?: boolean | null;
  address?: string | null;
}

export interface DeviceContact {
  contactId?: string;
  name?: DeviceContactName | null;
  phones?: DeviceContactPhone[] | null;
  emails?: DeviceContactEmail[] | null;
}

/** Colunas de `clients` geradas a partir de um contato (mesmo formato de `buildClientColumns`). */
export interface ContactClientColumns {
  name: string;
  phone: string | null;
  /** Sempre null: não importamos endereço da agenda (ver nota no topo). */
  address: null;
  email: string | null;
}

/**
 * Chave de comparação de telefone: só dígitos, sem o DDI 55 do Brasil.
 *
 * A agenda costuma guardar `+55 (11) 99999-9999` enquanto o app guarda
 * `(11) 99999-9999`; sem tirar o DDI a deduplicação não casaria. Só removemos
 * o `55` quando o resto tem tamanho de número brasileiro (10 ou 11 dígitos),
 * para não mutilar números internacionais.
 */
export function normalizePhone(value: string | null | undefined): string {
  const digits = (value ?? '').replace(/\D/g, '');
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    return digits.slice(2);
  }
  return digits;
}

/** Chave de comparação de nome: sem acento, sem espaço duplo, minúsculo. */
export function normalizeName(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/** Nome exibível do contato: `display` quando houver, senão given+middle+family. */
export function contactDisplayName(contact: DeviceContact): string {
  const n = contact.name;
  if (!n) return '';
  const display = (n.display ?? '').trim();
  if (display) return display;
  return [n.given, n.middle, n.family]
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

/** Telefone escolhido: o primeiro celular; sem celular, o primeiro com número. */
export function pickPhone(contact: DeviceContact): string | null {
  const phones = (contact.phones ?? []).filter((p) => (p?.number ?? '').trim());
  if (phones.length === 0) return null;
  const mobile = phones.find((p) => /mobile/i.test(p.type ?? ''));
  return (mobile ?? phones[0]).number!.trim();
}

/** E-mail escolhido: o primeiro com endereço preenchido. */
export function pickEmail(contact: DeviceContact): string | null {
  const email = (contact.emails ?? []).find((e) => (e?.address ?? '').trim());
  return email ? email.address!.trim() : null;
}

/**
 * Mapeia um contato para as colunas de `clients`.
 * Retorna `null` quando o contato não tem nome — nome é obrigatório na tabela
 * e um cliente sem nome é inútil na lista.
 */
export function contactToClientColumns(contact: DeviceContact): ContactClientColumns | null {
  const name = contactDisplayName(contact).replace(/\s+/g, ' ').trim();
  if (!name) return null;
  return {
    name,
    phone: pickPhone(contact),
    address: null,
    email: pickEmail(contact),
  };
}

/** Índice dos clientes já cadastrados, para checagem O(1) por contato. */
export interface ExistingClientIndex {
  phones: Set<string>;
  names: Set<string>;
}

export function buildExistingClientIndex(
  clients: Array<{ name?: string | null; phone?: string | null }>,
): ExistingClientIndex {
  const phones = new Set<string>();
  const names = new Set<string>();
  for (const c of clients) {
    const phone = normalizePhone(c.phone);
    if (phone) phones.add(phone);
    const name = normalizeName(c.name);
    if (name) names.add(name);
  }
  return { phones, names };
}

/**
 * O contato já virou cliente?
 *
 * Telefone é o sinal forte: se o número (só dígitos) já está na base, é o mesmo
 * cliente. Só caímos no nome quando o contato **não tem telefone** — casar por
 * nome mesmo com telefone diferente marcaria homônimos distintos como
 * duplicados e impediria o cadastro deles.
 */
export function isAlreadyRegistered(
  contact: DeviceContact,
  index: ExistingClientIndex,
): boolean {
  const phone = normalizePhone(pickPhone(contact));
  if (phone) return index.phones.has(phone);
  const name = normalizeName(contactDisplayName(contact));
  return name ? index.names.has(name) : false;
}

/**
 * Remove duplicatas internas da agenda (a mesma pessoa aparece 2x quando há
 * conta Google + contatos do SIM). Mantém a primeira ocorrência.
 *
 * Mesma regra do `isAlreadyRegistered`: telefone primeiro, nome só como
 * desempate para contatos sem telefone. Contatos sem nome são descartados aqui
 * (não viram cliente de qualquer forma).
 */
export function dedupeContacts(contacts: DeviceContact[]): DeviceContact[] {
  const seenPhones = new Set<string>();
  const seenNames = new Set<string>();
  const out: DeviceContact[] = [];

  for (const contact of contacts) {
    const name = normalizeName(contactDisplayName(contact));
    if (!name) continue;

    const phone = normalizePhone(pickPhone(contact));
    if (phone) {
      if (seenPhones.has(phone)) continue;
      seenPhones.add(phone);
      seenNames.add(name);
    } else {
      if (seenNames.has(name)) continue;
      seenNames.add(name);
    }
    out.push(contact);
  }

  return out;
}

/** Ordena por nome (pt-BR) para a lista do modal ficar previsível. */
export function sortContactsByName(contacts: DeviceContact[]): DeviceContact[] {
  return [...contacts].sort((a, b) =>
    contactDisplayName(a).localeCompare(contactDisplayName(b), 'pt-BR'),
  );
}

/** Filtro da busca do modal: casa por nome ou por telefone (com ou sem máscara). */
export function matchesContactSearch(contact: DeviceContact, term: string): boolean {
  const q = normalizeName(term);
  if (!q) return true;
  if (normalizeName(contactDisplayName(contact)).includes(q)) return true;
  const digits = q.replace(/\D/g, '');
  if (!digits) return false;
  return (contact.phones ?? []).some((p) => normalizePhone(p?.number).includes(digits));
}
